import { useEffect, useRef, type ReactNode } from "react";
import { formatParisDate, formatParisTime } from "@/lib/parisDateTime";
import type { TeamProfile } from "@/lib/useTeam";

export function personName(profilesById: Map<string, TeamProfile>, id: string | null | undefined): string {
  if (!id) return "—";
  const profile = profilesById.get(id);
  return profile?.full_name?.trim() || profile?.email || "Utilisateur inconnu";
}

export const ROLE_LABELS: Record<string, string> = {
  sales: "Commercial",
  sales_manager: "Manager",
  admin: "Admin",
};

/** « 04 nov. · 14:32 » en heure de Paris. */
export function formatShortDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return `${formatParisDate(value, { day: "2-digit", month: "short" })} · ${formatParisTime(value)}`;
}

export function formatPercent(value: number | null): string {
  return value == null ? "—" : `${Math.round(value * 100)} %`;
}

/** Âge d'une tâche en cours : ambre au-delà de 24 h, rouge au-delà de 3 jours. */
export function ageTone(ms: number | null): string {
  if (ms == null) return "text-gray-900";
  if (ms >= 3 * 86_400_000) return "text-red-700";
  if (ms >= 86_400_000) return "text-amber-700";
  return "text-gray-900";
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white">
      <header className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {actions}
      </header>
      {children}
    </section>
  );
}

export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = "Garder",
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-900/40" onMouseDown={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="manager-confirm-title"
        className="w-[420px] rounded-lg bg-white p-5 shadow-xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 id="manager-confirm-title" className="text-base font-semibold text-gray-900">
          {title}
        </h2>
        <div className="mt-2 text-sm text-gray-600">{children}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
          >
            {busy ? "Annulation…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
